import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, LogOut, ExternalLink, RotateCcw, Save } from "lucide-react";
import { api, fetchContent, formatApiError } from "../lib/api";
import { normalize } from "../lib/content";
import { AdminLogin } from "../components/admin/AdminLogin";
import { ClassEditor } from "../components/admin/ClassEditor";
import { PricingEditor, ContactEditor } from "../components/admin/SettingsEditors";
import { TextsEditor } from "../components/admin/TextsEditor";
import { LookEditor, SectionsEditor } from "../components/admin/LookEditor";
import { AnnouncementEditor } from "../components/admin/AnnouncementEditor";
import { applyTheme } from "../lib/content";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Logo } from "../components/site/Logo";

const newId = () => `class-${Math.random().toString(36).slice(2, 8)}`;

export default function Admin() {
  const [user, setUser] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.documentElement.dir = "ltr";
    document.documentElement.lang = "en";
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  useEffect(() => {
    if (user) fetchContent().then(setDraft);
  }, [user]);
  useEffect(() => { if (draft?.theme) applyTheme(draft.theme); }, [draft?.theme]);

  const update = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
  const setClass = (i, c) => update({ classes: draft.classes.map((x, j) => (j === i ? c : x)) });
  const move = (i, dir) => {
    const list = [...draft.classes];
    [list[i], list[i + dir]] = [list[i + dir], list[i]];
    update({ classes: list });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", draft);
      setDraft(normalize(data));
      setDirty(false);
      toast.success("Saved — the live page is updated.");
    } catch (e) {
      if (e?.response?.status === 401) setUser(false);
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const reset = async () => {
    if (!window.confirm("Restore the original classes, rates and contact details? Your edits will be replaced.")) return;
    try {
      const { data } = await api.post("/content/reset");
      setDraft(normalize(data));
      setDirty(false);
      toast.success("Restored original content.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => null); setUser(false); };

  if (user === null) return <div className="min-h-screen bg-[#0B0B0E]" />;
  if (user === false) return <AdminLogin onLogin={setUser} />;
  if (!draft) return <div className="grid min-h-screen place-items-center bg-[#0B0B0E] text-zinc-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0B0B0E] pb-24 text-white" data-testid="admin-page">
      <header className="sticky top-0 z-30 border-b border-white/[0.07] bg-[#0B0B0E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-3">
            <Logo src={draft?.theme?.logo} />
            <div>
              <p className="font-display text-lg font-bold leading-none">Content editor</p>
              <p className="mt-1 text-xs text-zinc-400">{dirty ? "Unsaved changes" : draft.updated_at ? `Last saved ${new Date(draft.updated_at).toLocaleString()}` : "All changes saved"}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-sm text-zinc-200 hover:border-white/30" data-testid="admin-view-site"><ExternalLink size={14} /> View site</a>
            <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-sm text-zinc-200 hover:border-white/30" data-testid="admin-reset-button"><RotateCcw size={14} /> Reset</button>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-sm text-zinc-200 hover:border-white/30" data-testid="admin-logout-button"><LogOut size={14} /> Log out</button>
            <button onClick={save} disabled={saving || !dirty} data-testid="admin-save-changes-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#CCFF00] px-5 py-2 text-sm font-bold text-black disabled:opacity-40">
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 pt-8">
        <Tabs defaultValue="classes">
          <TabsList className="mb-6 h-auto flex-wrap justify-start bg-white/[0.05]">
            <TabsTrigger value="classes" data-testid="admin-tab-classes">Classes & times</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Rates</TabsTrigger>
            <TabsTrigger value="announcement" data-testid="admin-tab-announcement">Announcement</TabsTrigger>
            <TabsTrigger value="texts" data-testid="admin-tab-texts">Texts</TabsTrigger>
            <TabsTrigger value="look" data-testid="admin-tab-look">Logo, colours & photos</TabsTrigger>
            <TabsTrigger value="sections" data-testid="admin-tab-sections">Sections</TabsTrigger>
            <TabsTrigger value="contact" data-testid="admin-tab-contact">Contact</TabsTrigger>
          </TabsList>
          <TabsContent value="classes" className="space-y-4">
            {draft.classes.map((c, i) => (
              <ClassEditor key={c.id} cls={c} index={i} total={draft.classes.length} onChange={(v) => setClass(i, v)}
                onMove={(dir) => move(i, dir)}
                onRemove={() => window.confirm(`Delete "${c.title}"?`) && update({ classes: draft.classes.filter((_, j) => j !== i) })} />
            ))}
            <button data-testid="admin-add-class-button"
              onClick={() => update({ classes: [...draft.classes, { id: newId(), title: "New class", title_ar: "", tag: "", tag_ar: "", style: "", style_ar: "", program: "specialty", color: "cyan", fee: "", fee_ar: "", image: "", sessions: [{ day: "Sat", start: "", end: "" }] }] })}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 py-5 text-zinc-200 hover:border-[#CCFF00] hover:text-[#CCFF00]">
              <Plus size={16} /> Add a class
            </button>
          </TabsContent>
          <TabsContent value="pricing"><PricingEditor pricing={draft.pricing} onChange={(p) => update({ pricing: p })} /></TabsContent>
          <TabsContent value="announcement"><AnnouncementEditor value={draft.announcement} onChange={(v) => update({ announcement: v })} /></TabsContent>
          <TabsContent value="texts"><TextsEditor texts={draft.texts} onChange={(v) => update({ texts: v })} /></TabsContent>
          <TabsContent value="look"><LookEditor theme={draft.theme} images={draft.images} onTheme={(v) => update({ theme: v })} onImages={(v) => update({ images: v })} /></TabsContent>
          <TabsContent value="sections"><SectionsEditor sections={draft.sections} onChange={(v) => update({ sections: v })} /></TabsContent>
          <TabsContent value="contact"><ContactEditor contact={draft.contact} onChange={(c) => update({ contact: c })} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
